(function () {
  'use strict';

  if (window.UnifiedNormalizer) {
    return;
  }

  const VERSION = '1.0.0';
  const LEGACY_DEBUG_EVENT_NAME =
    'unified:debug';

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function isDebugEnabled() {
    return getConfig().debug === true;
  }

  function getSchema() {
    return window.UnifiedSchema || null;
  }

  function performanceStart(
    label,
    meta
  ) {
    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.start ===
        'function'
    ) {
      return window.UnifiedPerformance.start(
        label,
        meta
      );
    }

    return null;
  }

  function performanceEnd(
    tokenId,
    meta
  ) {
    if (
      tokenId === null ||
      typeof tokenId === 'undefined'
    ) {
      return null;
    }

    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.end ===
        'function'
    ) {
      return window.UnifiedPerformance.end(
        tokenId,
        meta
      );
    }

    return null;
  }

  function emitLegacyEvent(
    type,
    data
  ) {
    const detail = {
      type: type,
      timestamp:
        new Date().toISOString(),
      data:
        data || {}
    };

    try {
      window.dispatchEvent(
        new CustomEvent(
          LEGACY_DEBUG_EVENT_NAME,
          {
            detail: detail
          }
        )
      );

      return detail;
    } catch (error) {
      console.error(
        '[Unified Normalizer] Legacy event dispatch failed',
        {
          type: type,
          error: error.message
        }
      );

      return null;
    }
  }

  function emitEvent(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data
        );
      } catch (error) {
        reportError(
          'error',
          'normalizer_event_bus_failed',
          'Unified Normalizer could not emit an event through Unified Bus.',
          {
            event_type: type,
            error: error.message
          }
        );
      }
    }

    return emitLegacyEvent(
      type,
      data
    );
  }

  function reportError(
    severity,
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors.report ===
        'function'
    ) {
      return window.UnifiedErrors.report(
        severity,
        code,
        message,
        details || {}
      );
    }

    const method =
      severity === 'warning'
        ? 'warn'
        : 'error';

    console[method](
      '[Unified Normalizer] ' +
        message,
      details || {}
    );

    return null;
  }

  function clonePayload(
    payload
  ) {
    if (
      !payload ||
      typeof payload !== 'object' ||
      Array.isArray(payload)
    ) {
      return {};
    }

    return Object.assign(
      {},
      payload
    );
  }

  function hasOwn(
    object,
    key
  ) {
    return Object.prototype
      .hasOwnProperty.call(
        object,
        key
      );
  }

  function valuesAreEqual(
    firstValue,
    secondValue
  ) {
    if (
      typeof Object.is ===
      'function'
    ) {
      return Object.is(
        firstValue,
        secondValue
      );
    }

    return (
      firstValue === secondValue ||
      (
        firstValue !== firstValue &&
        secondValue !== secondValue
      )
    );
  }

  function getFieldDefinition(
    eventName,
    fieldName
  ) {
    const schema =
      getSchema();

    if (
      !schema ||
      typeof schema.getFieldDefinition !==
        'function'
    ) {
      return null;
    }

    return schema.getFieldDefinition(
      eventName,
      fieldName
    );
  }

  function isSensitiveField(
    eventName,
    fieldName
  ) {
    const definition =
      getFieldDefinition(
        eventName,
        fieldName
      );

    return Boolean(
      definition &&
      definition.analytics ===
        'excluded_sensitive'
    );
  }

  function sanitizeChangeValue(
    eventName,
    fieldName,
    value
  ) {
    if (
      isSensitiveField(
        eventName,
        fieldName
      )
    ) {
      return '[redacted]';
    }

    if (
      value === null ||
      typeof value === 'undefined' ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return value;
    }

    if (Array.isArray(value)) {
      return (
        '[array:' +
        value.length +
        ']'
      );
    }

    return '[object]';
  }

  function createChange(
    eventName,
    fieldName,
    operation,
    beforeValue,
    afterValue,
    extra
  ) {
    return Object.assign(
      {
        field:
          fieldName,

        operation:
          operation,

        before:
          sanitizeChangeValue(
            eventName,
            fieldName,
            beforeValue
          ),

        after:
          sanitizeChangeValue(
            eventName,
            fieldName,
            afterValue
          )
      },
      extra || {}
    );
  }

  function normalizeTrim(
    value
  ) {
    if (
      typeof value !== 'string'
    ) {
      return value;
    }

    return value.trim();
  }

  function normalizeLowercase(
    value
  ) {
    if (
      typeof value !== 'string'
    ) {
      return value;
    }

    return value.toLowerCase();
  }

  function normalizeNumber(
    value
  ) {
    if (
      typeof value === 'number'
    ) {
      return value;
    }

    if (
      typeof value !== 'string'
    ) {
      return value;
    }

    const candidate =
      value.trim();

    if (!candidate) {
      return value;
    }

    const parsedValue =
      Number(candidate);

    if (
      !Number.isFinite(
        parsedValue
      )
    ) {
      return value;
    }

    return parsedValue;
  }

  function normalizeBoolean(
    value
  ) {
    if (
      typeof value === 'boolean'
    ) {
      return value;
    }

    if (value === 1) {
      return true;
    }

    if (value === 0) {
      return false;
    }

    if (
      typeof value !== 'string'
    ) {
      return value;
    }

    const candidate =
      value
        .trim()
        .toLowerCase();

    if (
      [
        'true',
        '1',
        'yes',
        'on'
      ].includes(candidate)
    ) {
      return true;
    }

    if (
      [
        'false',
        '0',
        'no',
        'off'
      ].includes(candidate)
    ) {
      return false;
    }

    return value;
  }

  function normalizePhone(
    value
  ) {
    if (
      typeof value !== 'string' &&
      typeof value !== 'number'
    ) {
      return value;
    }

    return String(value)
      .trim()
      .replace(/\D+/g, '');
  }

  function normalizeEmail(
    value
  ) {
    if (
      typeof value !== 'string'
    ) {
      return value;
    }

    return value
      .trim()
      .toLowerCase();
  }

  const normalizerRegistry = {
    trim:
      normalizeTrim,

    lowercase:
      normalizeLowercase,

    number:
      normalizeNumber,

    boolean:
      normalizeBoolean,

    phone:
      normalizePhone,

    email:
      normalizeEmail
  };

  function getSupportedNormalizers() {
    return Object.keys(
      normalizerRegistry
    );
  }

  function getAllowedFieldSet(
    eventName
  ) {
    const schema =
      getSchema();

    if (
      !schema ||
      typeof schema.getAllowedFields !==
        'function'
    ) {
      return new Set();
    }

    return new Set(
      schema.getAllowedFields(
        eventName
      )
    );
  }

  function getAliasTarget(
    fieldName
  ) {
    const schema =
      getSchema();

    if (!schema) {
      return null;
    }

    if (
      typeof schema.getAlias ===
        'function'
    ) {
      return schema.getAlias(
        fieldName
      );
    }

    if (
      schema.aliases &&
      schema.aliases[fieldName]
    ) {
      return schema.aliases[
        fieldName
      ];
    }

    return null;
  }

  function applyAliases(
    eventName,
    payload,
    changes,
    warnings
  ) {
    const result =
      clonePayload(payload);

    const aliasesApplied =
      [];

    const allowedFields =
      getAllowedFieldSet(
        eventName
      );

    Object.keys(result)
      .forEach(function (
        sourceField
      ) {
        const targetField =
          getAliasTarget(
            sourceField
          );

        if (
          !targetField ||
          targetField ===
            sourceField ||
          !allowedFields.has(
            targetField
          )
        ) {
          return;
        }

        const sourceValue =
          result[sourceField];

        const targetExists =
          hasOwn(
            result,
            targetField
          ) &&
          typeof result[
            targetField
          ] !== 'undefined';

        if (targetExists) {
          warnings.push({
            code:
              'alias_conflict',

            alias:
              sourceField,

            field:
              targetField,

            resolution:
              'canonical_value_preserved'
          });

          changes.push(
            createChange(
              eventName,
              targetField,
              'alias_conflict',
              sourceValue,
              result[
                targetField
              ],
              {
                alias:
                  sourceField,

                resolution:
                  'canonical_value_preserved'
              }
            )
          );

          delete result[
            sourceField
          ];

          return;
        }

        result[
          targetField
        ] = sourceValue;

        delete result[
          sourceField
        ];

        aliasesApplied.push({
          from:
            sourceField,

          to:
            targetField
        });

        changes.push(
          createChange(
            eventName,
            targetField,
            'alias',
            sourceValue,
            sourceValue,
            {
              alias:
                sourceField
            }
          )
        );
      });

    return {
      payload:
        result,

      aliases_applied:
        aliasesApplied
    };
  }

  function applyNormalizationRules(
    eventName,
    payload,
    changes,
    warnings
  ) {
    const schema =
      getSchema();

    const result =
      clonePayload(payload);

    const normalizationMap =
      schema.getNormalizationMap(
        eventName
      );

    Object.keys(
      normalizationMap
    ).forEach(function (
      fieldName
    ) {
      if (
        !hasOwn(
          result,
          fieldName
        )
      ) {
        return;
      }

      const rules =
        Array.isArray(
          normalizationMap[
            fieldName
          ]
        )
          ? normalizationMap[
              fieldName
            ]
          : [];

      rules.forEach(function (
        ruleName
      ) {
        const normalizer =
          normalizerRegistry[
            ruleName
          ];

        if (
          typeof normalizer !==
            'function'
        ) {
          warnings.push({
            code:
              'unknown_normalizer',

            field:
              fieldName,

            normalizer:
              ruleName
          });

          return;
        }

        const beforeValue =
          result[
            fieldName
          ];

        const afterValue =
          normalizer(
            beforeValue
          );

        if (
          valuesAreEqual(
            beforeValue,
            afterValue
          )
        ) {
          return;
        }

        result[
          fieldName
        ] = afterValue;

        changes.push(
          createChange(
            eventName,
            fieldName,
            ruleName,
            beforeValue,
            afterValue
          )
        );
      });

    });

    return result;
  }

  function buildFailureResult(
    eventName,
    payload,
    code,
    message
  ) {
    return {
      success:
        false,

      event:
        eventName,

      payload:
        clonePayload(
          payload
        ),

      changed:
        false,

      changes:
        [],

      aliases_applied:
        [],

      warnings:
        [],

      errors: [
        {
          code:
            code,

          message:
            message
        }
      ]
    };
  }

  function normalize(
    eventName,
    payload
  ) {
    const performanceToken =
      performanceStart(
        'normalizer.total',
        {
          event:
            eventName
        }
      );

    const schema =
      getSchema();

    if (
      !schema ||
      typeof schema.getNormalizationMap !==
        'function'
    ) {
      const failure =
        buildFailureResult(
          eventName,
          payload,
          'normalization_schema_unavailable',
          'Unified Schema normalization contract is unavailable.'
        );

      reportError(
        'error',
        failure.errors[0].code,
        failure.errors[0].message,
        {
          event:
            eventName
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            eventName,

          status:
            'error',

          reason:
            failure.errors[0].code
        }
      );

      return failure;
    }

    if (
      !payload ||
      typeof payload !== 'object' ||
      Array.isArray(payload)
    ) {
      const failure =
        buildFailureResult(
          eventName,
          payload,
          'normalization_payload_invalid',
          'Unified Normalizer requires a plain object payload.'
        );

      reportError(
        'warning',
        failure.errors[0].code,
        failure.errors[0].message,
        {
          event:
            eventName
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            eventName,

          status:
            'error',

          reason:
            failure.errors[0].code
        }
      );

      return failure;
    }

    if (
      typeof schema.getEventSchema ===
        'function' &&
      !schema.getEventSchema(
        eventName
      )
    ) {
      const failure =
        buildFailureResult(
          eventName,
          payload,
          'normalization_event_unknown',
          'Unified Normalizer received an event that is not defined in Unified Schema.'
        );

      reportError(
        'warning',
        failure.errors[0].code,
        failure.errors[0].message,
        {
          event:
            eventName
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            eventName,

          status:
            'error',

          reason:
            failure.errors[0].code
        }
      );

      return failure;
    }

    const changes =
      [];

    const warnings =
      [];

    const aliasResult =
      applyAliases(
        eventName,
        payload,
        changes,
        warnings
      );

    const normalizedPayload =
      applyNormalizationRules(
        eventName,
        aliasResult.payload,
        changes,
        warnings
      );

    const result = {
      success:
        true,

      event:
        eventName,

      payload:
        normalizedPayload,

      changed:
        changes.length > 0,

      changes:
        changes,

      aliases_applied:
        aliasResult.aliases_applied,

      warnings:
        warnings,

      errors:
        []
    };

    emitEvent(
      'event_normalized',
      {
        event:
          eventName,

        success:
          true,

        changed:
          result.changed,

        changes:
          result.changes,

        aliases_applied:
          result.aliases_applied,

        warnings:
          result.warnings,

        payload:
          result.payload
      }
    );

    performanceEnd(
      performanceToken,
      {
        event:
          eventName,

        status:
          'success',

        changed:
          result.changed,

        changes:
          result.changes.length,

        aliases:
          result.aliases_applied.length,

        warnings:
          result.warnings.length
      }
    );

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Normalizer] Payload normalized',
        {
          event:
            eventName,

          changed:
            result.changed,

          changes:
            result.changes.length,

          aliases:
            result.aliases_applied.length,

          warnings:
            result.warnings.length
        }
      );
    }

    return result;
  }

  const schema =
    getSchema();

  if (
    !schema ||
    typeof schema.getNormalizationMap !==
      'function'
  ) {
    console.error(
      '[Unified Normalizer] Initialization failed. unified-schema.js v1.1.0 or newer must load first.'
    );

    return;
  }

  window.UnifiedNormalizer = {
    version:
      VERSION,

    normalize:
      normalize,

    getSupportedNormalizers:
      getSupportedNormalizers
  };

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Normalizer] Normalizer initialized',
      {
        version:
          VERSION,

        schemaVersion:
          schema.version ||
          'unknown',

        supportedNormalizers:
          getSupportedNormalizers()
      }
    );
  }
})();